import React from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, X } from "lucide-react";

/**
 * MultiAngleControls — the "identify from multiple photos" toggle plus its
 * collected thumbnail strip and the inline "Identify from N shots" button.
 *
 * Extracted from Camera.jsx (iter62). Pure controlled component: parent owns
 * the state (multiAngle, multiShots, busy) and passes handlers down.
 */
export default function MultiAngleControls({
  multiAngle,
  onToggleMultiAngle,
  multiShots,
  onRemoveShot,
  onIdentifyMulti,
  busy,
  busyLabel,
}) {
  const { t } = useTranslation();

  return (
    <div
      className="mb-3 rounded-2xl bg-white border border-[#E8E5DF] px-3 py-2.5"
      data-testid="multi-angle-section"
    >
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-leaf-ink text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={multiAngle}
            onChange={(e) => onToggleMultiAngle(e.target.checked)}
            data-testid="multi-angle-toggle"
            className="w-4 h-4 accent-leaf"
          />
          <span className="font-medium">{t("camera.multiAngle")}</span>
        </label>
        {multiAngle && multiShots.length >= 2 && (
          <button
            onClick={onIdentifyMulti}
            disabled={busy}
            data-testid="identify-multi-button"
            className="rounded-full bg-leaf hover:bg-leaf-deep disabled:opacity-60 text-white text-xs px-4 py-2 flex items-center gap-1.5 transition"
          >
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
            {busy ? busyLabel : t("camera.identifyMulti", { n: multiShots.length })}
          </button>
        )}
      </div>
      {multiAngle && (
        <>
          <p className="mt-1 text-[11px] text-leaf-mist">{t("camera.multiAngleHelp")}</p>
          {multiShots.length > 0 && (
            <div className="mt-2 flex gap-2" data-testid="multi-shots">
              {multiShots.map((src, i) => (
                <div
                  key={`shot-${i}`}
                  className="relative w-14 h-14 rounded-xl overflow-hidden bg-parchment"
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => onRemoveShot(i)}
                    aria-label={t("camera.removeShotAria")}
                    data-testid={`remove-shot-${i}`}
                    className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-white/95 text-rust flex items-center justify-center"
                  >
                    <X className="w-3 h-3" strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
